import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useFeatures from 'hooks/useFeatures';
import { PriceItemFormValues } from 'types/invoice.types';
import { VATTypeEnum, type IUser } from 'types/users.types';
import getFieldState from 'utils/getFieldState';
import { multiplyBy1000 } from 'utils/math';

const useFormMeta = () => {
  const user = useGetCurrentCustomerData().data as IUser;

  const { VATType } = user;

  const isSubjectToVAT = VATType === VATTypeEnum.subjectToVAT;

  const defaultVATRate = useFeatures('revenue.default_vat_rate');

  const getFormMeta = (formValues: PriceItemFormValues) => {
    // if user is now franchisee and tries to edit or create a new item
    //  where the default value of the VAT rate is 0 or unset
    // we disable the field and fix the value to 0
    const shouldDisableVATRate =
      !isSubjectToVAT && !formValues._originalVATRate;
    const shouldShowZeroVATRate =
      !isSubjectToVAT && !formValues._originalVATRate;

    const VATRateField = getFieldState({
      name: 'VATRate',
      value: () => formValues.VATRate,
      isDisabled: shouldDisableVATRate,
      isApplicable: !!formValues.category?.admitsVAT,
      suggestedChanges: (self) => {
        const { name, isApplicable, value } = self;
        if (!isApplicable) return [{ name, value: null }];
        if (shouldShowZeroVATRate) return [{ name, value: 0 }];
        if (value(self) == null && typeof defaultVATRate === 'number') {
          return [{ name, value: multiplyBy1000(defaultVATRate) }];
        }
      },
    });

    return {
      VATRate: VATRateField,
    };
  };

  const getSuggestedChanges = (formValues: PriceItemFormValues) => {
    return Object.values(getFormMeta(formValues)).flatMap(
      (field) => field.suggestedChanges,
    );
  };

  return { getFormMeta, getSuggestedChanges };
};

export default useFormMeta;
